import React, { useState, useEffect } from "react";
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress 
} from "@material-ui/core";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const formatElapsedTime = (start) => {
  if (!start) return "00:00:00";
  const diff = Math.floor((new Date() - new Date(start)) / 1000);
  if (diff < 0) return "00:00:00";
  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

const UserPauseWidget = () => {
  const [isPaused, setIsPaused] = useState(false);
  const [activePause, setActivePause] = useState(null);
  const [pauses, setPauses] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPauseId, setSelectedPauseId] = useState("");
  const [loading, setLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState("00:00:00");

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    let timer;
    if (isPaused && activePause?.startAt) {
      setElapsedTime(formatElapsedTime(activePause.startAt));
      timer = setInterval(() => {
        setElapsedTime(formatElapsedTime(activePause.startAt));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isPaused, activePause]);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/user-pauses/status");
      setIsPaused(data.isPaused);
      setActivePause(data.activePause);
    } catch (err) {
      // Ignore initial errors gracefully
    }
  };

  const loadPauses = async () => {
    try {
      const { data } = await api.get("/pauses");
      setPauses(data);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenModal = () => {
    loadPauses();
    setModalOpen(true);
  };

  const handleStartPause = async () => {
    if (!selectedPauseId) return;
    setLoading(true);
    try {
      const { data } = await api.post("/user-pauses", { pauseId: selectedPauseId });
      setIsPaused(true);
      setActivePause({ ...data, pause: pauses.find(p => p.id === selectedPauseId) });
      toast.success("Pausa iniciada!");
      setModalOpen(false);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleEndPause = async () => {
    setLoading(true);
    try {
      await api.put("/user-pauses");
      setIsPaused(false);
      setActivePause(null);
      toast.success("Retornado da pausa!");
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  return (
    <>
      {isPaused ? (
        <Button
          variant="contained"
          style={{ backgroundColor: "#ff9800", color: "#fff", marginRight: 16 }}
          size="small"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
          onClick={handleEndPause}
          disabled={loading}
        >
          {activePause?.pause?.name ? `Pausado (${activePause.pause.name}): ${elapsedTime}` : `Em Pausa: ${elapsedTime}`}
        </Button>
      ) : (
        <Button
          variant="outlined"
          style={{ color: "#fff", borderColor: "#fff", marginRight: 16 }}
          size="small"
          startIcon={<PauseIcon />}
          onClick={handleOpenModal}
        >
          Pausar
        </Button>
      )}

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Começar Pausa</DialogTitle>
        <DialogContent dividers>
          <FormControl variant="outlined" fullWidth margin="dense">
            <InputLabel>Motivo da Pausa</InputLabel>
            <Select
              value={selectedPauseId}
              onChange={(e) => setSelectedPauseId(e.target.value)}
              label="Motivo da Pausa"
            >
              {pauses.map((pause) => (
                <MenuItem key={pause.id} value={pause.id}>
                  {pause.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleStartPause} color="primary" variant="contained" disabled={loading || !selectedPauseId}>
            {loading ? <CircularProgress size={24} /> : "Pausar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserPauseWidget;
